import { IncomingMessage } from 'http';
import { capitalize, filter, isNil, omitBy, startCase, trim } from 'lodash';
import {
  Icon,
  Icons,
  IconURL,
} from 'next/dist/lib/metadata/types/metadata-types';
import { CSSProperties } from 'react';

import { asText, ColorField, ImageField, SliceZone } from '@prismicio/client';

import { ILocalAddress } from '@/components';
import {
  FilterAttributeNameMapping,
  PRODUCT_FILTER_ATTRIBUTES,
  SORT_BY_FIELD_OPTION,
  STATES_OF_RESIDENCE,
  TEMPLATE_BASE_PATH,
} from '@/constants';
import {
  AccountType,
  Address,
  AgeRestriction,
  BlogPostDocument,
  CartLineItem,
  Closure,
  EnumCustomerType as CustomerType,
  DayPeriod,
  Discount,
  DiscountRateType,
  DiscountType,
  EcomState,
  EnumCustomerType,
  GeoType,
  GProduct,
  IBlogTags,
  IPrismicSlice,
  JaneSpecial,
  JaneSpecialKind,
  LabResult,
  LineItemInput,
  Maybe,
  OpeningHoursSpecification,
  OrderAddress,
  OrderAddressInput,
  OrderDiscountItem,
  OrderDiscountItemInput,
  OrderInput,
  PageDocumentSlice,
  PickupScheduleZone,
  Product,
  PromoCarouselDocumentDataCarouselItemsItem,
  PromoCarouselDocumentDataCarouselItemsItemWithID,
  QueryFilters,
  ReservationMode,
  ScheduleWorkingHours,
  ScheduleZone,
  SortByParams,
  SortBySelectOption,
  Store,
  StoreAgreement,
  StorePaymentSettings,
  StorePaymentType,
  TreezFilters,
  TreezSpecials,
  TreezStore,
  Variant,
  WeekDay,
} from '@/types';

import { KeyTextField } from '@prismicio/client';
import { format, parse } from 'date-fns';
import { evaluate as evaluateTemplate } from '../scripts/utils';
import {
  DefaultDeliverySchedules,
  DefaultPickupSchedules,
} from './defaultSchedules';
import { getSpecialLink } from './treez';
import { SortOptionalValue } from './defaultSort';

export const currencyFormat = (value: number) => {
  if (!value) {
    return 0;
  }

  const toFloat = value / 100;

  return toFloat === Math.floor(toFloat)
    ? Math.floor(toFloat)
    : toFloat.toFixed(2);
};

export const formatProductDiscountText = ({
  value,
  format = 'percent',
}: {
  value: Variant;
  format?: 'amount' | 'percent';
}): string | null => {
  if (!value?.discount_percent && !value?.discount_amount) return null;

  if (format === 'amount')
    return `$${currencyFormat(value?.discount_amount as number)} Off`;

  return value?.discount_percent
    ? `${Math.round(value?.discount_percent)}% Off`
    : null;
};

export const addressToSlug = (text: string) => {
  if (!text) {
    return '';
  }

  return trim(text.replace(/ /g, '+').replace(/,/g, ''));
};

export const stringToSlug = (text: string | KeyTextField) => {
  if (!text) {
    return '';
  }

  return trim(
    text
      .toLowerCase()
      // eslint-disable-next-line no-useless-escape
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, ''),
    '-',
  );
};

export const stringToSlugBrands = (text: string) => {
  if (!text) {
    return '';
  }

  return trim(
    text
      .toLowerCase()
      // eslint-disable-next-line no-useless-escape
      .replace(/[^\w\s.-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, ''),
    '-',
  );
};

export const scrollToTop = () => {
  window.scrollTo({ behavior: 'smooth', top: 0 });
};

export const getOperatingHours = () => {
  // Service hours. In this case from 8am to 11pm.
  return [8, 22];
};

export const personValidationRules = [
  {
    field: 'first_name',
    message: 'First name is required.',
    method: 'isEmpty',
    validWhen: false,
  },
  {
    field: 'last_name',
    message: 'Last name is required.',
    method: 'isEmpty',
    validWhen: false,
  },
  {
    field: 'address1',
    message: 'Invalid or incomplete address.',
    method: (_, state) => {
      return [
        'address1',
        'city',
        'country_code',
        'zip',
        'latitude',
        'longitude',
      ].every(field => state[field]);
    },
    validWhen: true,
  },
];

export const evaluate = <T>(val: T, elem): T => {
  return evaluateTemplate(val, elem);
};

export const formatWeightNumber = v => {
  return mapWeightLabelToGrams(v?.type);
};

const mapWeightLabelToGrams = (type: string) =>
  ({
    each: 0,
    eighth_ounce: 3.5,
    gram: 1,
    half_gram: 0.5,
    half_ounce: 14,
    ounce: 28,
    quarter_ounce: 7,
    two_gram: 2,
  }[type] ?? +type?.split('_')[0]);

export const formatNumber = number => {
  return Number(number).toLocaleString();
};

export const getServerUrl = ({ url, headers }: IncomingMessage) => {
  const protocol = headers.referer?.split('://')[0] || 'https';
  return `${protocol}://${headers.host}${url}`;
};

export const sanitizeObject = (object: Object) => {
  return omitBy(
    Object.keys(object || {}).reduce((acc, key) => {
      const currentValue = object[key];
      if (isNil(currentValue)) {
        return acc;
      }
      if (Array.isArray(currentValue)) {
        return {
          ...acc,
          [key]:
            typeof currentValue[0] === 'object'
              ? currentValue.map(sanitizeObject)
              : filter(currentValue, value => !isNil(value)),
        };
      }
      if (currentValue !== null && typeof currentValue === 'object') {
        return {
          ...acc,
          [key]: sanitizeObject(currentValue),
        };
      }
      return { ...acc, [key]: currentValue };
    }, {}),
    isNil,
  );
};

export const shouldHideNavigation = (path: string): boolean => {
  return ['/checkout', '/thank-you', '/checkin'].includes(path);
};

export const createLocationUrl = (path: string): string => {
  const location = TEMPLATE_BASE_PATH.LOCATION;

  return `${location}${path}`;
};

export const getRichTextText = (richText: any): string => {
  return richText?.[0]?.text || '';
};

export const getTreezSeoAddress = (store: TreezStore): Address => {
  const [streetAddress, addressLocality, addressCountry, postalCode] =
    store.full_address.split(',');

  return {
    addressCountry,
    addressLocality,
    postalCode,
    streetAddress,
  };
};

type seoHours = OpeningHoursSpecification | OpeningHoursSpecification[];

export const getTreezSeoOpeningHours = (store: TreezStore): seoHours => {
  if (!store?.delivery_hours?.length) {
    return [];
  }

  const specification: OpeningHoursSpecification[] = store.delivery_hours.map(
    (hours: DayPeriod): OpeningHoursSpecification => {
      return {
        closes: hours.period.to || '',
        dayOfWeek: hours.day,
        opens: hours.period.from || '',
      };
    },
  );

  return specification;
};

export const getFirstPTextAsDescrition = (richText: any): string => {
  let description: string =
    richText?.find(content => {
      return content.type === 'paragraph' && content.text;
    })?.text || '';

  if (description.length > 100) {
    description = description?.substring(0, 100).concat('...');
  }

  return description;
};

export const numberToShortString = (number: number): string => {
  if (number < 1000) {
    return String(number);
  }
  if (number < 1000000) {
    return `${(number / 1000).toFixed(1)}K`;
  }
  if (number < 1000000000) {
    return `${(number / 1000000).toFixed(1)}M`;
  }
  if (number < 1000000000000) {
    return `${(number / 1000000000).toFixed(1)}B`;
  }

  return undefined as unknown as string;
};

export const getBlogsTags = (blogs: BlogPostDocument<string>[]): IBlogTags => {
  const tags: IBlogTags = {};

  if (!blogs || !blogs.length) {
    return tags;
  }

  blogs.forEach(article => {
    if (article.tags.length === 0) {
      return;
    }

    article.tags.forEach(value => {
      if (!tags[value]) {
        tags[value] = { count: 1, slug: stringToSlug(value) };
      } else {
        tags[value].count += 1;
      }
    });
  });

  return tags;
};

export const getProductBundleSpecial = (
  specials: JaneSpecial[],
  product?: Product,
): JaneSpecial | undefined => {
  return specials
    .filter(j => j.special_type === 'bundle')
    .find(j => checkIfSpecialIsBundled(product!, j));
};

export const getBundledProducts = async (
  special: JaneSpecial,
  id: string,
): Promise<GProduct[]> => {
  // first filter and map bundled products
  const searchProducts = special?.bundle_products
    ?.filter(pr => pr !== id)
    ?.map(v => {
      return {
        product_id: v,
      };
    });

  if (!searchProducts?.length) {
    return [];
  }

  return [];

  // try {
  //     return []; //await getProductsByIdList(searchProducts);
  // } catch (error) {
  //     // eslint-disable-next-line no-console
  //     console.log(error);

  //     return [];
  // }
};

const checkIfSpecialIsBundled = (
  product: Product,
  special: JaneSpecial,
): boolean => {
  if (!product) {
    return false;
  }

  if (special.bundle_products?.length) {
    return special.bundle_products.find(b => b === product.id) !== undefined;
  }

  if (special.brands) {
    if (!special.brands.find(br => br === product.brand_name)) {
      return false;
    }
  }

  if (special.kinds) {
    return (
      special.kinds.find(kd => checkSpeciakKind(kd, product)) !== undefined
    );
  }

  return true;
};

const checkSpeciakKind = (kind: JaneSpecialKind, product: Product): boolean => {
  if (product.cannabis_type?.toLowerCase() !== kind.kind?.toLowerCase()) {
    return false;
  }

  if (kind.brand_subtype) {
    return kind.brand_subtype === product.brand_subtype;
  }

  return true;
};

export const formatEnUSPhoneNumber = (phone: string) => {
  if (!phone) {
    return '';
  }

  const match = phone
    ?.split('+1')
    ?.at(-1)
    ?.replace(/\D/g, '')
    ?.match(/(\d{0,3})(\d{0,3})(\d{0,4})/);

  return !match?.[2]
    ? `+1 ${match?.[1]}`
    : '+1 (' + match[1] + ') ' + match[2] + (match[3] ? '-' + match[3] : '');
};

export function toQueryFilters(input: TreezFilters): QueryFilters {
  const queryFilters: QueryFilters = {};

  for (const attribute in input) {
    if (input.hasOwnProperty(attribute)) {
      const values = input[attribute].values ?? input[attribute]?.value;
      queryFilters[attribute] = {};

      if (Array.isArray(values)) {
        values.forEach(value => {
          queryFilters[attribute][value] = true;
        });
      } else if (typeof values === 'string') {
        queryFilters[attribute][values] = true;
      }
    }
  }

  return queryFilters;
}

export function toTreezSortBy(
  queryString?: string,
): SortBySelectOption | undefined {
  if (!queryString) {
    return undefined;
  }

  // Regular expression to separate camelCase string
  const match = queryString.match(/([a-zA-Z]+)(Asc|Desc)$/);

  if (!match) {
    return undefined;
  }

  const [, field] = match;

  const filterOption = SORT_BY_FIELD_OPTION?.find(
    option => (option?.value as SortByParams)?.field === field,
  );

  return filterOption;
}

export function toQuerySortBy(selectOption: SortBySelectOption): string {
  if (selectOption.value === 'default') {
    return undefined as unknown as string;
  }

  return `${selectOption.value.field}${startCase(selectOption.value.order)}`;
}

const getVariantPrice = (salePrice: number, price: number): number => {
  if (!salePrice) {
    return price;
  }

  if (salePrice > price) {
    return price;
  }

  return salePrice;
};

const getAccountType = (customerType: CustomerType) =>
  ({
    [CustomerType.ADULT]: AccountType.Adult,
    [CustomerType.MEDICAL]: AccountType.Medical,
  }[customerType] ?? AccountType.Adult);

export const getOrderFromEcomState = (
  state: EcomState,
  store: TreezStore,
): OrderInput => {
  return {
    entity_id: state.entity_id,
    email: state.email,
    date_of_birth: state.date_of_birth,

    // payment
    payment_details: state.payment_details,

    // payment address
    delivery_address: state.delivery_address,

    // delivery details
    delivery_details: state.delivery_details,

    // discount items
    discount_items: state.discount_items.map(
      (i: OrderDiscountItem): OrderDiscountItemInput => ({
        blaze_id: i.blaze_id,
        discountType: i.discountType as DiscountType,
        discount_apply_type: i.discount_apply_type,
        discount_rate_type: i.discount_rate_type as DiscountRateType,
        id: i.id as string,
        promo_code: i.promo_code,
        rate: i.rate as number,
      }),
    ),

    // line items
    line_items: mapLineItems(state.line_items),

    notes: state.notes,
    buyer_accepts_marketing: state.buyer_accepts_marketing,

    // provider info
    provider_store_full_address: store?.full_address,
    provider_store_id: store?.shortName,
    provider_store_name: store?.name,
    provider_store_phone_number: store?.phone,

    // totals
    subtotal_price: state.subtotal_price,
    total_delivery: state.total_delivery,
    total_discounts: state.total_discounts,
    total_line_items_price: state.total_line_items_price,
    total_price: state.total_price,
    total_service_fee: state.total_service_fee,
    total_tax: state.total_tax,

    // medical info
    order_type: getAccountType(state.customer_type),
    order_medical_id_expiration_date: '',
    order_medical_id: '',

    // alpine iq
    alpineiq_template_id: state.alpineiq_template_id,
    alpineiq_redemption_url: state.alpineiq_redemption_url,

    //treez order
    treez_order_id: state.treez_order_id,
    treez_order_number: state.treez_order_number,

    treez_pay_ticket_ids_by_store: state.treez_pay_ticket_ids_by_store,

    //state of residence
    state_of_residence: state.state_of_residence,
  };
};

export const mapLineItems = (lineItems: CartLineItem[]): LineItemInput[] => {
  if (!lineItems?.length) {
    return [];
  }

  return lineItems.map(
    (lineItem: CartLineItem): LineItemInput => ({
      brand: lineItem.brand_name,
      category: lineItem.category,
      id: lineItem?.id?.toString(),
      item_id: lineItem?.id?.toString(),
      pos_id: lineItem?.id?.toString(),
      base_weight: lineItem?.base_weight,
      tier_method: lineItem?.tier_method,
      name: lineItem.name,
      price: lineItem.weightVariant.price,
      quantity: lineItem.quantity,
      sale_price: lineItem.weightVariant
        ? lineItem.weightVariant.salePrice
        : lineItem.sales_price,
      thumbnail: lineItem.asset?.image,
      price_type: lineItem.price_type,
      variants: [
        {
          id: lineItem?.weightVariant?.id?.toString(),
          name: lineItem.weightVariant.name,
          price: getVariantPrice(
            lineItem?.weightVariant?.salePrice as number,
            lineItem?.weightVariant?.price as number,
          ),
          type: lineItem.weightVariant.type,
        },
      ],
    }),
  );
};

export function encryptText(text) {
  return text;
  // const key = process.env.NEXT_PUBLIC_ENCRYPTION_KEY;
  // const textEncryption = CryptoJS.AES.encrypt(text, key).toString();

  // return textEncryption;
}

export function decryptText(textEncrypt) {
  return textEncrypt;

  // const key = process.env.NEXT_PUBLIC_ENCRYPTION_KEY;
  // const bytes = CryptoJS.AES.decrypt(textEncrypt, key);
  // const textOrigin = bytes.toString(CryptoJS.enc.Utf8);

  // return textOrigin;
}

export function getFormatedPhoneNumber(phoneNumber: string) {
  if (!phoneNumber) {
    return;
  }
  //This is for check when the phone number includes +1
  if (phoneNumber.slice(0, 2) === '+1') {
    return phoneNumber
      .replace(/\D/g, '')
      .replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '+$1 ($2) $3-$4');
  }
  return phoneNumber
    .replace(/\D/g, '')
    .replace(/(\d{3})(\d{3})(\d{4})/, '+1 ($1) $2-$3');
}

export const getCrmListIds = crm => crm?.map(v => v?.value?.toString());

export const textAlign = (
  alignment: 'bottom' | 'left' | 'right' | 'top' | 'center',
) => {
  switch (alignment) {
    case 'left':
    case 'top':
      return 'flex-start';
    case 'right':
    case 'bottom':
      return 'flex-end';
    default:
      return 'center';
  }
};

export const alignContent = (align: 'center' | 'start' | 'end' = 'center') => {
  switch (align) {
    case 'center':
      return 'center';
    default:
      return `flex-${align}`;
  }
};

export const getContentAlign = (
  contentAlign: 'start' | 'left' | 'center' | 'end' | 'right',
) => {
  switch (contentAlign) {
    case 'start':
    case 'left':
      return 'flex-start';
    case 'end':
    case 'right':
      return 'flex-end';
    default:
      return 'center';
  }
};

type PropertyName = keyof CSSProperties;

export const getCSSpropety = <T extends PropertyName>(
  name: T,
  value: CSSProperties[T] | null,
) => {
  return value ? { [name]: value } : {};
};

export const getSlicePadding = (
  padding: 'small' | 'medium' | 'large' = 'large',
) => `padding-slice-${padding}`;

export const getFavicon = (
  iconURL: string | undefined | null,
): null | IconURL | Array<Icon> | Icons => {
  if (!iconURL) {
    return null;
  }

  return {
    icon: iconURL,
    shortcut: iconURL,
    apple: iconURL,
  };
};

export const stripHtml = html => {
  // Use a regular expression to remove all HTML tags.
  return html.replace(/<[^>]*>?/gm, '');
};

const defaultPaymentSettings: StorePaymentSettings[] = [
  {
    reservation_mode: ReservationMode.All,
    payment_type: StorePaymentType.Cash,
    stores_provider_ids: [],
  },
  {
    reservation_mode: ReservationMode.All,
    payment_type: StorePaymentType.Debit,
    stores_provider_ids: [],
  },
  {
    reservation_mode: ReservationMode.All,
    payment_type: StorePaymentType.Ach,
    stores_provider_ids: [],
  },
  {
    reservation_mode: ReservationMode.All,
    payment_type: StorePaymentType.Credit,
    stores_provider_ids: [],
  },
];

export const filterResModAndProviderStoreId = (
  s: StorePaymentSettings | StoreAgreement | Closure | null,
  providerStoreId?: string,
  res?: ReservationMode,
): boolean => {
  return !!(
    s?.stores_provider_ids?.includes(providerStoreId!) &&
    (s?.reservation_mode === res || s?.reservation_mode === ReservationMode.All)
  );
};

export const filterStoreContent = (
  store: Store,
  providerStoreId?: string,
  isPickup?: boolean | null,
  deliveryAddress?: OrderAddressInput | null,
  isExpress?: boolean,
): Store => {
  const res = isPickup ? ReservationMode.Pickup : ReservationMode.Delivery;

  // filter payment settings
  const payment_settings = store?.payment_settings?.filter(s =>
    filterResModAndProviderStoreId(s, providerStoreId, res),
  );

  // filter agreements
  const agreements = store?.agreements?.filter(s =>
    filterResModAndProviderStoreId(s, providerStoreId, res),
  );

  // filter closures
  const closures = store?.closures?.filter(s =>
    filterResModAndProviderStoreId(s, providerStoreId, res),
  );

  // pick up hours
  const pickup_schedules = store?.pickup_schedules?.filter(
    s => s?.stores_provider_id === providerStoreId,
  );

  let delivery_schedules = DefaultDeliverySchedules;

  // we only need to filter this if it is a delivery order
  // becasue we are not using this info at checkout
  if (res === ReservationMode.Delivery) {
    const f = getDeliveryScheduleByAddress(
      {
        latitude: deliveryAddress?.latitude || 0,
        longitude: deliveryAddress?.longitude || 0,
        zip: deliveryAddress?.zip || '',
      },
      store,
    );

    if (f) {
      delivery_schedules = [f];
    }
  }

  let ds = delivery_schedules;

  // filter express delivery times slots if the zone
  // offers express delivery
  if ((ds[0] as ScheduleZone)?.express_menu_config?.active) {
    ds = ds?.map(ds => {
      return {
        ...ds,
        time_slots: ds.time_slots?.filter(ts =>
          isExpress ? ts?.is_express : !ts?.is_express,
        ),
      };
    });
  }

  const ps = pickup_schedules?.length
    ? pickup_schedules
    : DefaultPickupSchedules;

  return {
    ...store,
    delivery_schedules: ds as ScheduleZone[],
    pickup_schedules: ps as PickupScheduleZone[],
    payment_settings: payment_settings?.length
      ? payment_settings
      : defaultPaymentSettings,
    agreements,
    closures,
  };
};

export const checkOrderForCustomDeliveryFee = (
  gcStoreWithGeoInfo: Store,
  order: OrderInput,
  store?: TreezStore,
) => {
  // this is a config from treez store settings that allows
  // customer to use a custom delivery fee
  const res = order?.delivery_details?.pickup
    ? ReservationMode.Pickup
    : ReservationMode.Delivery;

  if (res === ReservationMode.Delivery && store?.allowCustomDeliveryFee) {
    // can can ref delivery schedule on zero index because is gets default
    // before we call this method
    order.allow_custom_delivery_fee = true;
    order.total_delivery =
      gcStoreWithGeoInfo?.delivery_schedules?.[0]?.delivery_fee ?? 0;

    const freeAfter =
      gcStoreWithGeoInfo?.delivery_schedules?.[0]?.free_after ?? -1;

    if (freeAfter > 0) {
      // calculate order subtotal
      const subtotal = order?.line_items?.reduce((sum, p) => {
        const v = p?.variants?.[0];

        const price = v?.price ?? 0;
        const quantity = p?.quantity ?? 0;

        return sum + price * quantity;
      }, 0);

      if ((subtotal ?? 0) > freeAfter) {
        order.total_delivery = 0; // fee delivery
      }
    }
  }
};

export const getDeliveryScheduleByAddress = (
  address: ILocalAddress,
  store: Store,
) => {
  try {
    const ds = store?.delivery_schedules?.find(schedule => {
      switch (schedule?.geo_type) {
        case GeoType.Zip:
          return schedule?.zips?.includes(address.zip as string);
        case GeoType.Geofence:
          return isPointInsidePolygon(address, schedule?.polygon_geo_zone);
        case GeoType.Radius:
          return isPointInsideCircle(address, store, schedule?.radius ?? 0);
        default:
          return undefined;
      }
    });

    return ds ?? (DefaultDeliverySchedules[0] as ScheduleZone);
  } catch (error) {
    return DefaultDeliverySchedules[0] as ScheduleZone;
  }
};

const isPointInsidePolygon = (
  deliveryAddress: ILocalAddress,
  polygonGeoZone?: string | null,
) => {
  if (!polygonGeoZone) return false;

  let isInsidePolygon = false;
  try {
    const point = new google.maps.LatLng(
      deliveryAddress.latitude || 0,
      deliveryAddress.longitude || 0,
    );

    const polygon: google.maps.Polygon = new google.maps.Polygon({
      paths: JSON.parse(polygonGeoZone).map(
        ({ lat, lng }: { lat: number; lng: number }) => ({ lat, lng }),
      ),
    });

    isInsidePolygon = google.maps.geometry.poly.containsLocation(
      point,
      polygon,
    );

    return isInsidePolygon;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('invalid polygonGeoZone JSON string', error);
    return false;
  }
};

const isPointInsideCircle = (
  deliveryAddress: ILocalAddress,
  store: Store,
  radius: number,
) => {
  try {
    const point = new google.maps.LatLng(
      deliveryAddress.latitude || 0,
      deliveryAddress.longitude || 0,
    );

    const center = new google.maps.LatLng(
      store?.latitude || 0,
      store?.longitude || 0,
    );

    const distance = google.maps.geometry.spherical.computeDistanceBetween(
      point,
      center,
    );

    const distanceInMiles = metersToMiles(distance);

    return distanceInMiles <= radius;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('invalid radius config', err);

    return false;
  }
};

const metersToMiles = (meters: number) => meters / 1609.344;

export const getDeliveryScheduleByZip = (zip: string, store: Store) => {
  const ds = store?.delivery_schedules?.find(schedule =>
    schedule?.zips?.includes(zip as string),
  );

  return ds;
};

export const getBogoSpecialURL = (
  d: Pick<Discount, 'discount_title' | 'discount_id'>,
): string => {
  return `${TEMPLATE_BASE_PATH.SPECIALS}${stringToSlug(
    d?.discount_title,
  )}?special_id=${d?.discount_id}`;
};

export const getBogoFromProduct = (product: GProduct): Discount | undefined =>
  product?.discounts?.find(
    item => item?.discount_method?.toUpperCase() === 'BOGO',
  );

export const getBundleFromProduct = (product: GProduct): Discount | undefined =>
  product?.discounts?.find(
    item => item?.discount_method?.toUpperCase() === 'BUNDLE',
  );

export const formatPhoneNumber = (phone: string) => {
  const phoneNumber = phone.match(/^[^(]+/);
  return phoneNumber ? phoneNumber[0].trim() : '';
};

export const getMaxWidthForDevice = (
  currentWidth: number,
  desktopValue: number | undefined,
  mobileValue: number | undefined,
) => {
  if (currentWidth > 640 && desktopValue) {
    return `${desktopValue}px`;
  }

  if (currentWidth <= 640 && mobileValue) {
    return `${mobileValue}px`;
  }

  return;
};

export const getPaymentMethods = (
  store: Store | undefined,
): StorePaymentType[] => {
  const paymentTypes = store?.payment_settings
    ?.map((payment: Maybe<StorePaymentSettings>) => payment?.payment_type)
    ?.filter(Boolean) as StorePaymentType[];

  return [...new Set(paymentTypes)];
};

export const isNullish = (value: any): value is null | undefined =>
  value === null || value === undefined;

export const getActivesPreCartDiscounts = (
  cmsDiscountsItems?: PromoCarouselDocumentDataCarouselItemsItem[],
  preCartTreezDiscounts?: TreezSpecials[],
) => {
  if (!cmsDiscountsItems?.length) return {};

  const defaultCMSDiscountItem = cmsDiscountsItems?.find(
    item => item?.pos_title_name?.toLocaleLowerCase()?.trim() === 'default',
  );

  return cmsDiscountsItems?.reduce<
    Record<string, PromoCarouselDocumentDataCarouselItemsItemWithID>
  >((result, current, index) => {
    if (!current?.pos_title_name) {
      return {
        ...result,
        [`ad-${index}`]: {
          ...current,
        },
      };
    }

    const currentPostTitleName = current?.pos_title_name
      ?.toLocaleLowerCase()
      ?.trim();

    const currentCMSDiscountTitle = current?.title?.trim();

    if (currentPostTitleName === 'default') {
      preCartTreezDiscounts?.forEach(treezDiscount => {
        if (!result?.[treezDiscount?.id]) {
          result = {
            ...result,
            [treezDiscount?.id]: {
              ...evaluate(current, treezDiscount),
              brands: treezDiscount?.brands,
              categories: treezDiscount?.categories,
              id: treezDiscount?.id,
              title:
                treezDiscount?.displayTitle?.trim() ||
                treezDiscount?.title?.trim(),
              treezDiscountTitle: treezDiscount?.displayTitle,
              treezDiscountDescription: treezDiscount?.description,
              imageUrl: treezDiscount?.imageUrl,
              displayImageOnly: treezDiscount?.displayImageOnly,
              areTreezTitleAndDescriptionPresent: Boolean(
                treezDiscount.displayTitle && treezDiscount.description,
              ),
            },
          } as {
            [key: string]: PromoCarouselDocumentDataCarouselItemsItemWithID;
          };
        }
      });

      return result;
    }

    const treezDiscount = preCartTreezDiscounts?.find(
      special =>
        special?.title?.toLocaleLowerCase().trim() === currentPostTitleName,
    );

    if (!treezDiscount) return result;

    return {
      ...result,
      [treezDiscount.id]: {
        ...evaluate(current, treezDiscount),
        background_color:
          current?.background_color ??
          (defaultCMSDiscountItem?.background_color as ColorField),
        brands: treezDiscount?.brands,
        categories: treezDiscount?.categories,
        id: treezDiscount?.id,
        title:
          currentCMSDiscountTitle ||
          treezDiscount?.displayTitle?.trim() ||
          treezDiscount?.title?.trim(),
        treezDiscountTitle: treezDiscount?.displayTitle,
        treezDiscountDescription: treezDiscount?.description,
        imageUrl: treezDiscount?.imageUrl,
        picture: current?.picture?.url
          ? current?.picture
          : (defaultCMSDiscountItem?.picture as ImageField),
        displayImageOnly: treezDiscount?.displayImageOnly,
        areTreezTitleAndDescriptionPresent: Boolean(
          treezDiscount.displayTitle && treezDiscount.description,
        ),
        definedInPrismic: true,
      },
    };
  }, {});
};

export const getSpecialURL = (
  special: PromoCarouselDocumentDataCarouselItemsItemWithID,
) => {
  // if id is undefined is because the special is a item without a POS Title Name
  if (special?.id) {
    return getSpecialLink(special);
  }

  return special?.cta_link ?? '/';
};

export const allowedAge = (
  customerType: EnumCustomerType,
  r?: AgeRestriction,
): number => {
  // this case will be for when there is not store selected
  if (!r) {
    return 21;
  }

  if (customerType === CustomerType.MEDICAL) {
    return r?.medical;
  }

  return r?.adult;
};

export const getTaxValue = (displayPostTax?: boolean, tax?: number) => {
  if (displayPostTax) {
    return 'Included';
  }

  if (!tax) {
    return 'Free';
  }

  return `$${currencyFormat(tax)}`;
};

export const convertSlugToTitle = (slug: string): string => {
  try {
    return slug
      .split('-')
      .map(i => capitalize(i))
      .join(' ');
  } catch (error) {
    return slug;
  }
};

export const getCartItemsSubtotal = (cartItems: CartLineItem[]) => {
  return cartItems.reduce((sum, { weightVariant: v, quantity }) => {
    const salePrice = v?.salePrice ?? 0;
    const price = v?.price ?? 0;
    return sum + (salePrice < price ? salePrice : price) * quantity;
  }, 0);
};

export const getCartItemsCount = (cartItems: CartLineItem[]) =>
  cartItems?.reduce((sum, current) => sum + (current?.quantity ?? 0), 0) ?? 0;

export const formatAddress = ({
  address,
  format = 'long',
}: {
  address?: OrderAddress;
  format: 'short' | 'long';
}) => {
  const shortFormat = [address?.address1, address?.address2, address?.city];
  const longFormat = [
    address?.address1,
    address?.address2,
    address?.city,
    address?.province,
    address?.zip,
    address?.country_code,
  ];

  if (format === 'short') return shortFormat?.filter(Boolean).join(', ') || '';

  return longFormat?.filter(Boolean).join(', ') || '';
};

export const getGoogleApiKey = (googleMapsApi?: string) => {
  if (!googleMapsApi) return '';

  try {
    const urlObj = new URL(googleMapsApi);
    return urlObj.searchParams.get('key') ?? '';
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('invalid googleApiUrl', error);
    return '';
  }
};

export const getQueryByText = (
  special?: PromoCarouselDocumentDataCarouselItemsItemWithID,
  searchTerm?: string,
) =>
  [asText(special?.description), special?.pos_title_name, special?.title].some(
    text => text?.toLowerCase()?.includes(searchTerm?.toLowerCase() ?? ''),
  );

const formatNumberType = (num: number): string => {
  const truncated = Math.floor(num * 100) / 100;
  return truncated % 1 === 0
    ? `${truncated.toFixed(0)}`
    : `${truncated.toFixed(2)}`;
};

const formatValue = (value: number, amountType: string): string => {
  const suffix = amountType === 'PERCENTAGE' ? '%' : amountType;
  return `${formatNumber(value)}${suffix}`;
};

export const getCompoundDisplayValue = (
  type: 'THC' | 'CBD' | 'TAC',
  product: GProduct,
  itShowDetailAvg: boolean = false,
): string | null => {
  if (!product) return null;

  const labParam = type === 'TAC' ? 'TOTAL_CANNABINOIDS' : type;
  const labResult = product.labResults?.find(r => r?.result_type === labParam);

  if (labResult?.amount && labResult.amount_type) {
    const { minimum_value, maximum_value } = labResult.amount;
    const { amount_type } = labResult;

    const min = parseFloat(minimum_value);
    const max = parseFloat(maximum_value);

    const bothInvalid = isNaN(min) || isNaN(max);
    const bothZero = min === 0 && max === 0;
    const sameValue = min === max;

    // If itShowDetailAvg is true, show the full range
    if (itShowDetailAvg && !bothInvalid && !bothZero) {
      if (sameValue) {
        return `${formatValue(min, amount_type)}`;
      }
      return `${formatValue(min, amount_type)} - ${formatValue(
        max,
        amount_type,
      )}`;
    }

    // Show range if min and max are valid and distinct
    if (!bothInvalid && !bothZero && !sameValue) {
      const avg = (min + max) / 2;
      return `${formatValue(avg, amount_type)} AVG.`;
    }

    // Show single value if valid, same value and type is TAC
    if (!bothInvalid && sameValue && type === 'TAC') {
      return `${formatValue(min, amount_type)}`;
    }
  }

  // If laboratory data are unavailable (or invalid), use the product data.
  if (type !== 'TAC') {
    const compound = product[type.toLowerCase() as 'thc' | 'cbd'];
    if (compound?.percentage) {
      return `${formatNumberType(compound.percentage)}%`;
    }
    if (compound?.amount) {
      return `${formatNumberType(compound.amount)}MG`;
    }
  }

  return null;
};

export const formatSpecialFlowerType = (flowerType?: string) => {
  const specialTypes = ['SI', 'IS'];

  if (!flowerType) {
    return null;
  }

  const processedInput = flowerType?.replace(/\s+/g, '')?.toUpperCase();

  if (specialTypes.includes(processedInput)) {
    return processedInput?.split('')?.join('/');
  }

  return flowerType;
};

export const formatSizeFilter = (name: string) => {
  const match = name.match(/^([a-zA-Z]+)_(\d+(?:\.\d+)?)$/);

  if (!match) return '';

  const [, unit, value] = match;
  return `${value}${unit.toUpperCase()}`;
};

export const normalizeSizeLabel = (label: string): string => {
  if (!label) return '';
  return label
    .trim()
    .toLowerCase()
    .replace(/,/g, '.')
    .replace(/\s+/g, ' ')
    .replace(/^(\d+(?:\.\d+)?)\s+([a-z]+)/i, '$1$2'); // join number + first word
};

export const getStateOfResidenceLabel = (
  stateOfResidence: string | undefined,
) => {
  return STATES_OF_RESIDENCE?.find(state => state.value === stateOfResidence)
    ?.label;
};

export const getReadableLabel = (url?: string | null): string => {
  if (!url) return 'External site';

  try {
    const hasProtocol = url.startsWith('http://') || url.startsWith('https://');
    const normalizedUrl = hasProtocol ? url : `https://${url}`;

    const u = new URL(normalizedUrl);
    const domain = u.hostname.replace(/^www\./, '').split('.')[0];

    return domain.charAt(0).toUpperCase() + domain.slice(1);
  } catch {
    return 'External site';
  }
};

type FormattedSchedule = {
  day: string;
  period: string;
};

/**
 * Processes an array of weekly working hours and groups consecutive days
 * that have identical open/closed schedules. For example, if Monday-Friday
 * all have the hours "9:00 AM - 5:00 PM", they will be condensed into a
 * single entry: { day: "Mon - Fri", period: "9:00 AM - 5:00 PM" }.
 *
 * @param {ScheduleWorkingHours[] | undefined} schedules The raw array of schedule objects.
 * @returns {FormattedSchedule[]} A condensed array of formatted schedule strings.
 */

export const getStoreScheduleInfo = (
  schedules?: ScheduleWorkingHours[],
): FormattedSchedule[] => {
  if (!schedules?.length) return [];

  const daysOfWeek: { long: WeekDay; short: string }[] = [
    { long: WeekDay.Monday, short: 'Mon' },
    { long: WeekDay.Tuesday, short: 'Tue' },
    { long: WeekDay.Wednesday, short: 'Wed' },
    { long: WeekDay.Thursday, short: 'Thu' },
    { long: WeekDay.Friday, short: 'Fri' },
    { long: WeekDay.Saturday, short: 'Sat' },
    { long: WeekDay.Sunday, short: 'Sun' },
  ];

  const scheduleMap = schedules.reduce((map, schedule) => {
    if (schedule.day_of_the_week) {
      map.set(schedule.day_of_the_week, schedule);
    }
    return map;
  }, new Map<WeekDay, ScheduleWorkingHours>());

  const processedWeek = daysOfWeek.map(dayInfo => {
    const schedule = scheduleMap.get(dayInfo.long);
    const period = getPeriodString(schedule);
    return { short: dayInfo.short, period };
  });

  const result: FormattedSchedule[] = [];
  for (let i = 0; i < processedWeek.length; i++) {
    const currentDay = processedWeek[i];
    let endGroupIndex = i;

    for (let j = i + 1; j < processedWeek.length; j++) {
      if (processedWeek[j].period === currentDay.period) {
        endGroupIndex = j;
      } else {
        break;
      }
    }

    const dayString =
      i === endGroupIndex
        ? currentDay.short
        : `${currentDay.short} - ${processedWeek[endGroupIndex].short}`;

    result.push({ day: dayString, period: currentDay.period });
    i = endGroupIndex;
  }

  return result;
};

// Takes a schedule object and returns a formatted time range string (e.g., "9:00 AM - 5:00 PM").
// Returns 'Closed' if the hours are not provided.
const getPeriodString = (
  schedule: ScheduleWorkingHours | undefined,
): string => {
  if (!schedule?.start || !schedule.end) {
    return 'Closed';
  }

  try {
    const startDate = parse(schedule.start, 'HH:mm', new Date());
    const endDate = parse(schedule.end, 'HH:mm', new Date());
    return `${format(startDate, 'h:mm a')} - ${format(endDate, 'h:mm a')}`;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error formatting date:', error);
    return 'Invalid Time';
  }
};

// Robust function to extract RGB from any valid CSS value
export const parseRGB = (color: string): [number, number, number] | null => {
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) return null;

  ctx.fillStyle = color;
  const computed = ctx.fillStyle;

  const match = computed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!match) return null;

  return [parseInt(match[1]), parseInt(match[2]), parseInt(match[3])];
};

export const aspectRatioToPaddingTop = (
  ratioStr: string,
): string | undefined => {
  const overrides: Record<string, string> = {
    '4:3': '76.27',
    '1:1': '100',
  };

  if (!ratioStr || ratioStr === 'default') {
    return undefined;
  }

  if (ratioStr in overrides) {
    return `${overrides[ratioStr]}%`;
  }

  const [w, h] = ratioStr.split(':').map(Number);

  if (isNaN(w) || isNaN(h) || w === 0) {
    throw new Error(`Aspect ratio inválido: ${ratioStr}`);
  }

  const result = (h / w) * 100;
  return `${result.toFixed(2)}%`;
};

// Dimendions to aspectRatio
export const gcd = (a: number, b: number): number => {
  a = Math.trunc(Math.abs(a));
  b = Math.trunc(Math.abs(b));
  while (b !== 0) [a, b] = [b, a % b];
  return a || 1; // evita devolver 0
};

export const dimensionsToAspectRatio = (width: number, height: number) => {
  const g = gcd(width, height);
  return `${width / g}/${height / g}`;
};

//Merge the external and internal stores with an attribute that differentiates them: externalStore
export type TreezAndExternalStore = TreezStore & {
  externalStore?: boolean;
};

export const mergeStoresWithFlag = (
  stores: TreezStore[] = [],
  externalStores: TreezStore[] = [],
): TreezAndExternalStore[] => {
  const externalWithFlag = externalStores.map(store => ({
    ...store,
    externalStore: true,
  }));

  return [...stores, ...externalWithFlag];
};

export const sanitizeTreezSearchParams = ({
  searchParams,
}: {
  searchParams: Record<string, string | string[] | boolean[]>;
}) => {
  const filterAttributes = Object.values(PRODUCT_FILTER_ATTRIBUTES);
  // this will return an array in the format of [["brand", "Stiiizy" ], ["category", "FLOWER,EDIBLE"]]
  // then we map the keys to the correct attribute name if needed (brand -> brand.keyword)
  const mappedSearchParamsEntries = Object.entries(searchParams).map(
    ([key, value]) => {
      const mappedKey = FilterAttributeNameMapping[key] ?? key;
      return [mappedKey, value] as [
        PRODUCT_FILTER_ATTRIBUTES,
        string | string[] | boolean[],
      ];
    },
  );
  // we filter the entries to only have the valid filter attributes
  const validSearchParamsEntries = mappedSearchParamsEntries.filter(([key]) =>
    filterAttributes.includes(key),
  );

  return Object.fromEntries(validSearchParamsEntries);
};

export const getSortedCannabinoidsCompounds = (compound: LabResult[]) =>
  compound?.sort((a, b) => {
    const valueA = parseFloat(a?.amount?.maximum_value);
    const valueB = parseFloat(b?.amount?.maximum_value);

    if (isNaN(valueA) || isNaN(valueB)) {
      return 0;
    }

    return valueB - valueA;
  }) || compound;

/**
 * Generic helper for "any" headless CMS document that has slices.
 * - Preserves the type of the input document (TDoc).
 * - Ensures that all slices have the same type (TSlice).
 * - If you pass `sort`, it adds/updates `data.sort_products_on_page`.
 */
export function resolveDocument<TDoc extends PageDocumentSlice>(
  doc: TDoc,
  slices: SliceZone<IPrismicSlice>,
  sort?: SortOptionalValue,
  bottomSlices?: SliceZone<IPrismicSlice>,
): TDoc {
  const next: TDoc = {
    ...doc,
    data: {
      ...doc.data,
      slices,
      ...(sort !== undefined ? { sort_products_on_page: sort } : {}),
      bottomSlices,
    },
  };
  return next;
}
